import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ContactInput, Contact } from '../types';
import { apiService } from '../services/api';
import { validatePhone } from '../utils/phoneUtils';
import { ContactCategorySelector } from './ContactCategorySelector';
import { ContactLabelSelector } from './ContactLabelSelector';

interface Category {
  id: string;
  nome: string;
  funnelId?: string | null;
  funnelOrder?: number | null;
  funnel?: { id: string; nome: string } | null;
}

const contactSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(validatePhone, {
    message: 'Formato de telefone inválido',
  }),
  followUpAtivo: z.enum(['ativo', 'inativo']).optional(),
  iaAtiva: z.enum(['ativo', 'inativo']).optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  endereco: z.string().optional(),
  cnpj: z.string().optional(),
  instagram: z.string().optional(),
  facebook: z.string().optional(),
  youtube: z.string().optional(),
  tiktok: z.string().optional(),
  twitter: z.string().optional(),
  linkedin: z.string().optional(),
  threads: z.string().optional(),
  bluesky: z.string().optional(),
  reddit: z.string().optional(),
  telegram: z.string().optional(),
  googleBusiness: z.string().optional(),
  observacoes: z.string().optional(),
  resumoIA: z.string().optional(),
  categoriaId: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

const socialFields: Array<{ key: keyof ContactFormData; label: string }> = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'twitter', label: 'X/Twitter' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'threads', label: 'Threads' },
  { key: 'bluesky', label: 'Bluesky' },
  { key: 'reddit', label: 'Reddit' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'googleBusiness', label: 'Google Business' },
];

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ContactForm({ contact, onSuccess, onCancel }: ContactFormProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selectedLabels, setSelectedLabels] = useState<string[]>(contact?.tags || []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nome: contact?.nome || '',
      telefone: contact?.telefone || '',
      followUpAtivo: contact?.followUpAtivo ? 'ativo' : 'inativo',
      iaAtiva: contact?.iaAtiva ? 'ativo' : 'inativo',
      email: contact?.email || '',
      endereco: contact?.endereco || '',
      cnpj: contact?.cnpj || '',
      instagram: contact?.instagram || '',
      facebook: contact?.facebook || '',
      youtube: contact?.youtube || '',
      tiktok: contact?.tiktok || '',
      twitter: contact?.twitter || '',
      linkedin: contact?.linkedin || '',
      threads: contact?.threads || '',
      bluesky: contact?.bluesky || '',
      reddit: contact?.reddit || '',
      telegram: contact?.telegram || '',
      googleBusiness: contact?.googleBusiness || '',
      observacoes: contact?.observacoes || '',
      resumoIA: contact?.resumoIA || '',
      categoriaId: contact?.categoriaId || '',
    },
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onCancel();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isSubmitting, onCancel]);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/categorias/all', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : (data.categories || []));
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const selectedCategoriaId = watch('categoriaId') || '';
  const onSubmit = async (data: ContactFormData) => {
    try {
      const contactInput: ContactInput = {
        nome: data.nome,
        telefone: data.telefone,
        followUpAtivo: data.followUpAtivo === 'ativo',
        iaAtiva: data.iaAtiva === 'ativo',
        email: data.email || undefined,
        endereco: data.endereco || undefined,
        cnpj: data.cnpj || undefined,
        instagram: data.instagram || undefined,
        facebook: data.facebook || undefined,
        youtube: data.youtube || undefined,
        tiktok: data.tiktok || undefined,
        twitter: data.twitter || undefined,
        linkedin: data.linkedin || undefined,
        threads: data.threads || undefined,
        bluesky: data.bluesky || undefined,
        reddit: data.reddit || undefined,
        telegram: data.telegram || undefined,
        googleBusiness: data.googleBusiness || undefined,
        observacoes: data.observacoes || undefined,
        resumoIA: data.resumoIA || undefined,
        categoriaId: data.categoriaId || undefined,
        tags: selectedLabels,
      };

      if (contact) {
        await apiService.updateContact(contact.id, contactInput);
        toast.success('Contato atualizado com sucesso');
      } else {
        await apiService.createContact(contactInput);
        toast.success('Contato criado com sucesso');
      }

      onSuccess();
    } catch (err: any) {
      // Verificar se é erro de quota
      if (err?.isQuotaError || err?.upgradeRequired) {
        toast.error(err.message || 'Limite de contatos atingido. Faça upgrade do seu plano para continuar.', {
          duration: 6000,
          icon: '!'
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar contato';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/60 p-2 backdrop-blur-sm sm:p-4" onMouseDown={(event) => { if (event.target === event.currentTarget && !isSubmitting) onCancel(); }}>
      <div className="flex max-h-[96dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/20 bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="form-title">
        <div className="relative flex flex-shrink-0 items-center gap-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-indigo-50 px-4 py-4 pr-16 sm:px-6">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div className="min-w-0">
            <h2 id="form-title" className="text-lg font-bold text-gray-900 sm:text-xl">{contact ? 'Editar Contato' : 'Novo Contato'}</h2>
            <p className="truncate text-sm text-gray-500">{contact ? 'Atualize as informações do contato' : 'Preencha os dados para criar um novo contato'}</p>
          </div>
          <button type="button" onClick={onCancel} disabled={isSubmitting} className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50" aria-label="Fechar modal" title="Fechar">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:space-y-5 sm:px-6">
          <div>
            <label htmlFor="nome" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              Nome *
            </label>
            <input
              id="nome"
              type="text"
              {...register('nome')}
              className="input-field text-sm sm:text-base"
              placeholder="Digite o nome completo"
            />
            {errors.nome && (
              <p className="text-red-500 text-xs sm:text-sm mt-1">
                {errors.nome.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="telefone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              Telefone *
            </label>
            <input
              id="telefone"
              type="tel"
              {...register('telefone')}
              disabled={Boolean(contact)}
              placeholder="+55 11 99999-9999"
              className={`input-field text-sm sm:text-base ${contact ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            />
            {errors.telefone && (
              <p className="text-red-500 text-xs sm:text-sm mt-1">
                {errors.telefone.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="followUpAtivo" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              Follow-up
            </label>
            <select
              id="followUpAtivo"
              {...register('followUpAtivo')}
              className="input-field text-sm sm:text-base"
            >
              <option value="inativo">Inativo</option>
              <option value="ativo">Ativo</option>
            </select>
          </div>

          <div>
            <label htmlFor="iaAtiva" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              IA do contato
            </label>
            <select
              id="iaAtiva"
              {...register('iaAtiva')}
              className="input-field text-sm sm:text-base"
            >
              <option value="inativo">Desativar IA</option>
              <option value="ativo">Ativar IA</option>
            </select>
          </div>

          <div>
            <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              {...register('email')}
              className="input-field text-sm sm:text-base"
              placeholder="Digite o email"
            />
            {errors.email && (
              <p className="text-red-500 text-xs sm:text-sm mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="cnpj" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              CNPJ
            </label>
            <input
              id="cnpj"
              type="text"
              {...register('cnpj')}
              className="input-field text-sm sm:text-base"
              placeholder="00.000.000/0000-00"
            />
          </div>

          <div>
            <label htmlFor="endereco" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              Endereço
            </label>
            <input
              id="endereco"
              type="text"
              {...register('endereco')}
              className="input-field text-sm sm:text-base"
              placeholder="Rua, número, bairro, cidade - UF"
            />
          </div>

          <div>
            <div className="mb-2 text-xs sm:text-sm font-semibold text-gray-700">Redes sociais</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {socialFields.map((field) => (
                <div key={String(field.key)}>
                  <label htmlFor={String(field.key)} className="block text-xs font-semibold text-gray-600 mb-1.5">{field.label}</label>
                  <input
                    id={String(field.key)}
                    type="text"
                    {...register(field.key)}
                    className="input-field text-sm sm:text-base"
                    placeholder={`Perfil ${field.label}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {loadingCategories
            ? <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500">Carregando categorias...</div>
            : <ContactCategorySelector categories={categories} value={selectedCategoriaId} onChange={(categoriaId) => setValue('categoriaId', categoriaId, { shouldDirty: true })} label="Categoria" />}

          <ContactLabelSelector value={selectedLabels} onChange={setSelectedLabels} label="Etiquetas" />

          <div>
            <label htmlFor="observacoes" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              Observações
            </label>
            <textarea
              id="observacoes"
              {...register('observacoes')}
              rows={3}
              className="input-field resize-none text-sm sm:text-base"
              placeholder="Digite observações adicionais"
            />
          </div>

          <div>
            <label htmlFor="resumoIA" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
              Resumo IA
            </label>
            <textarea
              id="resumoIA"
              {...register('resumoIA')}
              rows={4}
              className="input-field resize-none text-sm sm:text-base"
              placeholder="Resumo usado pela IA para entender o contato"
            />
          </div>

          <div className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-3 border-t border-gray-200 bg-white/95 px-4 pb-1 pt-4 backdrop-blur sm:-mx-6 sm:flex-row sm:gap-4 sm:px-6">
            <button
              type="button"
              onClick={onCancel}
              className="w-full sm:flex-1 bg-gray-100 text-gray-700 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200 text-sm sm:text-base"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full sm:flex-1 py-2.5 sm:py-3 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </>
              ) : (
                'Salvar Contato'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


